import { createAsyncThunk } from '@reduxjs/toolkit';
import { ImportProfessionalsActions } from 'application/constants/actions';
import { AxiosError } from 'axios';
import { ImportProfessionalsApi } from 'integration/api/importProfessionals';
import { GetImportedDoctorsParams } from 'common/types/importedDoctors';
import {
  ExternalProfessionalCreationDto,
  ExternalProfessionalUpdateDto,
} from '@docbay/schemas';

/**
 * Fetches the list of imported professionals.
 *
 * @param params - The parameters for fetching imported professionals.
 * @returns An async thunk that fetches the list of imported professionals.
 */
export const fetchImportedProfessionals = createAsyncThunk(
  ImportProfessionalsActions.fetchImportedProfessionals,
  async (params: GetImportedDoctorsParams, { rejectWithValue }) => {
    try {
      const { data: responseData } =
        await ImportProfessionalsApi.fetchImportedProfessionals(params);
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Gets an external professional by ID.
 *
 * @param id - The ID of the external professional.
 * @returns An async thunk that gets an external professional by ID.
 */
export const getExternalProfessionalById = createAsyncThunk(
  ImportProfessionalsActions.getExternalProfessionalById,
  async (id: string, { rejectWithValue }) => {
    try {
      const { data } = await ImportProfessionalsApi.getExternalProfessionalById(
        id,
      );
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Adds a new professional.
 *
 * @param data - The data for the new professional.
 * @returns An async thunk that adds a new professional.
 */
export const addProfessionals = createAsyncThunk(
  ImportProfessionalsActions.addProfessionals,
  async (data: ExternalProfessionalCreationDto, { rejectWithValue }) => {
    try {
      const { data: responseData } =
        await ImportProfessionalsApi.addProfessionals(data);
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Updates an existing professional.
 *
 * @param id - The ID of the professional to update.
 * @param data - The updated data for the professional.
 * @returns An async thunk that updates an existing professional.
 */
export const updateProfessionals = createAsyncThunk(
  ImportProfessionalsActions.updateProfessionals,
  async (
    { id, data }: { id: string; data: ExternalProfessionalUpdateDto },
    { rejectWithValue },
  ) => {
    try {
      const { data: responseData } =
        await ImportProfessionalsApi.updateProfessionals(id, data);
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Deletes a professional.
 *
 * @param id - The ID of the professional to delete.
 * @returns An async thunk that deletes a professional.
 */
export const deleteProfessionals = createAsyncThunk(
  ImportProfessionalsActions.deleteProfessionals,
  async (id: string, { rejectWithValue }) => {
    try {
      const { data: responseData } =
        await ImportProfessionalsApi.deleteProfessionals(id);
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Uploads professionals.
 *
 * @param data - The FormData containing the professional information to upload.
 * @returns An async thunk that uploads professionals.
 */
export const uploadProfessionals = createAsyncThunk(
  ImportProfessionalsActions.uploadProfessionals,
  async (data: FormData, { rejectWithValue }) => {
    try {
      const { data: responseData } =
        await ImportProfessionalsApi.uploadProfessionals(data);
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Gets the upload status.
 *
 * @returns An async thunk that gets the upload status.
 */
export const uploadStatus = createAsyncThunk(
  ImportProfessionalsActions.uploadStatus,
  async (_, { rejectWithValue }) => {
    try {
      const { data: responseData } =
        await ImportProfessionalsApi.uploadStatus();
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);
